import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from '../auth/gateway';
import { useDevice } from '../contexts/DeviceContext';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { fetchSignedMediaUrl } from '../utils/media';
import type { 
  DashboardData, 
  DateRange, 
  GpsData, 
  PhotoData, 
  AudioData, 
  TranscriptData,
  WebSocketMessage
} from '../types/dashboard';

export const useDashboardData = (isRealtime = false, selectedHour: number | null = null) => {
  const { selectedDevice } = useDevice();
  const [data, setData] = useState<DashboardData>({
    gps: [],
    photos: [],
    audio: [],
    transcripts: [],
    loading: false,
    debugData: {}
  });
  const [audioLoading, setAudioLoading] = useState<Record<string, boolean>>({});
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [photoMeta, setPhotoMeta] = useState({
    total: 0,
    currentPage: 1,
    pageSize: 20,
    hasMore: false
  });
  const [photoLoading, setPhotoLoading] = useState(false);
  const [realtimeApiCalls, setRealtimeApiCalls] = useState<Array<{id: string, type: string, url: string, timestamp: Date, status: 'loading' | 'success' | 'error'}>>([]);
  
  // デバッグ用：realtimeApiCallsの変更を監視
  useEffect(() => {
    console.log('🔍 realtimeApiCalls updated:', realtimeApiCalls);
  }, [realtimeApiCalls]);


  const getDateRange = useCallback((dateStr: string, hour?: number | null): DateRange => {
    const [year, month, day] = dateStr.split('-');
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    if (hour !== null && hour !== undefined) {
      // 指定時間の1時間範囲（ローカル時刻基準）
      const startLocal = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), hour);
      const endLocal = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), hour + 1);
      
      return { start: startLocal.toISOString(), end: endLocal.toISOString() };
    } else {
      // 全日（ローカル時刻基準）
      const startLocal = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());
      const endLocal = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999);
      
      return { start: startLocal.toISOString(), end: endLocal.toISOString() };
    }
  }, []);

  const getTodayRange = useCallback((): DateRange => {
    return getDateRange(selectedDate, selectedHour);
  }, [selectedDate, selectedHour, getDateRange]);

  const fetchTodayData = useCallback(async () => {
    if (!selectedDevice) return;
    
    setData(prev => ({ ...prev, loading: true }));
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No token for dashboard data request');
        return;
      }
      
      const { start, end } = getTodayRange();
      
      const [gpsResponse, photoResponse, audioResponse] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/gnss/snapshots?from_time=${start}&to_time=${end}&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos?from_time=${start}&to_time=${end}&limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio?from_time=${start}&to_time=${end}&limit=40${!isRealtime ? '&processingState=success' : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const debug: any = {};
      let gpsData: GpsData[] = [];
      let photoData: PhotoData[] = [];
      let audioData: AudioData[] = [];
      let transcriptData: TranscriptData[] = [];
      
      // GPS データ処理
      if (gpsResponse.ok) {
        const gpsResult = await gpsResponse.json();
        debug.gps = gpsResult;
        gpsData = gpsResult.snapshots || [];
      }
      
      // 写真データ処理
      if (photoResponse.ok) {
        const photoResult = await photoResponse.json();
        debug.photos = photoResult;
        const photos = photoResult.photos || [];
        
        // ページング情報を保存
        setPhotoMeta({
          total: photoResult.total || photos.length,
          currentPage: 1,
          pageSize: photoResult.limit || 20,
          hasMore: photoResult.has_more || false
        });
        
        // サムネイルURLを認証付きで取得してBlobURLを作成
        const thumbnailPromises = photos.map(async (photo: PhotoData) => {
          try {
            const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/photos/${photo.id}/thumbnail`, token);
            const response = await fetch(signedUrl);
            if (!response.ok) {
              throw new Error(`Failed to download thumbnail for ${photo.id}`);
            }
            const blob = await response.blob();
            photo.thumbnail_url = URL.createObjectURL(blob);
          } catch (error) {
            console.error(`Failed to fetch thumbnail ${photo.id}:`, error);
          }
        });
        
        await Promise.all(thumbnailPromises);
        photoData = photos;
      }
      
      // 音声データ処理
      if (audioResponse.ok) {
        const audioResult = await audioResponse.json();
        debug.audio = audioResult;
        const audioFiles = audioResult.audio_files || [];
        audioData = audioFiles;
        
        // 転写データを取得
        if (audioFiles.length > 0) {
          const transcriptPromises = audioFiles.map((audio: AudioData) =>
            fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audio.id}/transcripts`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
          );
          
          const transcriptResponses = await Promise.all(transcriptPromises);
          const allTranscripts: TranscriptData[] = [];
          const transcriptResults: any[] = [];
          
          for (let i = 0; i < transcriptResponses.length; i++) {
            const response = transcriptResponses[i];
            if (response.ok) {
              const result = await response.json();
              transcriptResults.push(result);
              const audioId = audioFiles[i].id;
              const transcripts = result.transcripts || [];
              transcripts.forEach((transcript: any) => {
                allTranscripts.push({ ...transcript, audio_id: audioId });
              });
            }
          }
          
          // 音声ファイルのBlobURL作成は遅延読み込み（再生時に実行）
          
          debug.transcripts = transcriptResults;
          transcriptData = allTranscripts;
        }
      }
      
      setData({
        gps: gpsData,
        photos: photoData,
        audio: audioData,
        transcripts: transcriptData,
        loading: false,
        debugData: debug
      });
      
    } catch (error) {
      secureLogger.error('Failed to fetch dashboard data', { error: 'Dashboard fetch failed' });
      setData(prev => ({ ...prev, loading: false }));
    }
  }, [selectedDevice, isRealtime, getTodayRange]);

  const loadAudioFile = useCallback(async (audioId: string) => {
    if (!selectedDevice) return;
    
    setAudioLoading(prev => ({ ...prev, [audioId]: true }));
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/audio/${audioId}/file`, token);
      
      setData(prev => ({
        ...prev,
        audio: prev.audio.map(audio => 
          audio.id === audioId 
            ? { ...audio, s3_url: signedUrl }
            : audio
        )
      }));
    } catch (error) {
      secureLogger.error('Failed to fetch audio file');
    } finally {
      setAudioLoading(prev => ({ ...prev, [audioId]: false }));
    }
  }, [selectedDevice]);

  const addNewAudio = useCallback(async (audioId: string) => {
    if (!selectedDevice) return;
    
    const callId = `audio_${audioId}_${Date.now()}`;
    const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}`;
    
    console.log('🎵 addNewAudio: Setting realtimeApiCalls loading state');
    // API呼び出し開始をログ
    setRealtimeApiCalls(prev => {
      const newCalls = [{
        id: callId,
        type: 'audio_detail',
        url,
        timestamp: new Date(),
        status: 'loading' as const
      }, ...prev.slice(0, 9)];
      console.log('🎵 addNewAudio: New realtimeApiCalls:', newCalls);
      return newCalls;
    }); // 最新10件まで保持
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;
      
      // 音声詳細を直接取得
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const newAudio = await response.json();
        
        // 成功をログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'success' as const } : call
        ));
        
        setData(prev => {
          const exists = prev.audio.find(a => a.id === newAudio.id);
          if (exists) {
            return {
              ...prev,
              audio: prev.audio.map(a => a.id === newAudio.id ? newAudio : a)
            };
          }
          return {
            ...prev,
            audio: [newAudio, ...prev.audio]
          };
        });
      } else {
        // エラーをログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'error' as const } : call
        ));
      }
    } catch (error) {
      console.error('Failed to fetch new audio:', error);
      // エラーをログ
      setRealtimeApiCalls(prev => prev.map(call => 
        call.id === callId ? { ...call, status: 'error' as const } : call
      ));
    }
  }, [selectedDevice]);
  
  const updateTranscript = useCallback(async (transcriptId: string, audioId?: string) => {
    if (!selectedDevice) return;
    
    const callId = `transcript_${transcriptId}_${Date.now()}`;
    const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}/transcripts/${transcriptId}`;
    
    // API呼び出し開始をログ
    setRealtimeApiCalls(prev => [{
      id: callId,
      type: 'transcript_detail',
      url,
      timestamp: new Date(),
      status: 'loading'
    }, ...prev.slice(0, 9)]);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const transcript = await response.json();
        const targetAudioId = transcript.audio_id || audioId;
        
        // 成功をログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'success' as const } : call
        ));
        
        setData(prev => ({
          ...prev,
          transcripts: prev.transcripts.some(t => t.id === transcript.id)
            ? prev.transcripts.map(t => t.id === transcript.id ? { ...transcript, audio_id: targetAudioId } : t)
            : [{ ...transcript, audio_id: targetAudioId }, ...prev.transcripts]
        }));
      } else {
        // エラーをログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'error' as const } : call
        ));
      }
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
      // エラーをログ
      setRealtimeApiCalls(prev => prev.map(call => 
        call.id === callId ? { ...call, status: 'error' as const } : call
      ));
    }
  }, [selectedDevice]);

  const addNewPhoto = useCallback(async (photoId: string) => {
    if (!selectedDevice) return;
    
    const callId = `photo_${photoId}_${Date.now()}`;
    const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos/${photoId}`;
    
    // API呼び出し開始をログ
    setRealtimeApiCalls(prev => [{
      id: callId,
      type: 'photo_detail',
      url,
      timestamp: new Date(),
      status: 'loading'
    }, ...prev.slice(0, 9)]);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;
      
      // 写真詳細を取得
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const newPhoto = await response.json();
        
        // サムネイルURLを取得
        try {
          const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/photos/${photoId}/thumbnail`, token);
          const thumbResponse = await fetch(signedUrl);
          if (!thumbResponse.ok) {
            throw new Error('Signed thumbnail download failed');
          }
          const blob = await thumbResponse.blob();
          newPhoto.thumbnail_url = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Failed to fetch thumbnail ${photoId}:`, error);
        }
        
        // 成功をログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'success' as const } : call
        ));
        
        // 重複チェック後、リストの先頭に追加
        if (newPhoto && !data.photos.find(p => p.id === newPhoto.id)) {
          setData(prev => ({
            ...prev,
            photos: [newPhoto, ...prev.photos]
          }));
        }
      } else {
        // エラーをログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'error' as const } : call
        ));
      }
    } catch (error) {
      console.error('Failed to fetch new photo:', error);
      // エラーをログ
      setRealtimeApiCalls(prev => prev.map(call => 
        call.id === callId ? { ...call, status: 'error' as const } : call
      ));
    }
  }, [selectedDevice, data.photos]);

  const addNewGps = useCallback(async (gpsId: string) => {
    if (!selectedDevice) return;
    
    const callId = `gps_${gpsId}_${Date.now()}`;
    const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/gnss/snapshots/${gpsId}`;
    
    // API呼び出し開始をログ
    setRealtimeApiCalls(prev => [{
      id: callId,
      type: 'gps_detail',
      url,
      timestamp: new Date(),
      status: 'loading'
    }, ...prev.slice(0, 9)]);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;
      
      // GPS詳細を取得
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const newGps = await response.json();
        
        // 成功をログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'success' as const } : call
        ));
        
        // 重複チェック後、リストの先頭に追加
        if (newGps && !data.gps.find(g => g.measured_at === newGps.measured_at)) {
          setData(prev => ({
            ...prev,
            gps: [newGps, ...prev.gps]
          }));
        }
      } else {
        // エラーをログ
        setRealtimeApiCalls(prev => prev.map(call => 
          call.id === callId ? { ...call, status: 'error' as const } : call
        ));
      }
    } catch (error) {
      console.error('Failed to fetch new GPS:', error);
      // エラーをログ
      setRealtimeApiCalls(prev => prev.map(call => 
        call.id === callId ? { ...call, status: 'error' as const } : call
      ));
    }
  }, [selectedDevice, data.gps]);

  // デバッグ用テスト関数
  const testApiCall = useCallback(() => {
    console.log('🧪 testApiCall called');
    const callId = `test_${Date.now()}`;
    setRealtimeApiCalls(prev => {
      const newCalls = [{
        id: callId,
        type: 'test_call',
        url: 'https://example.com/test',
        timestamp: new Date(),
        status: 'success' as const
      }, ...prev.slice(0, 9)];
      console.log('🧪 testApiCall: New realtimeApiCalls:', newCalls);
      return newCalls;
    });
  }, []);

  const loadPhotoPage = useCallback(async (page: number) => {
    if (!selectedDevice) return;
    
    setPhotoLoading(true);
    const offset = (page - 1) * photoMeta.pageSize;
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;
      
      const { start, end } = getDateRange(selectedDate, selectedHour);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos?from_time=${start}&to_time=${end}&limit=${photoMeta.pageSize}&offset=${offset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        const photos = result.photos || [];
        
        // サムネイルURLを取得
        const thumbnailPromises = photos.map(async (photo: PhotoData) => {
          try {
            const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/photos/${photo.id}/thumbnail`, token);
            const thumbResponse = await fetch(signedUrl);
            if (!thumbResponse.ok) {
              throw new Error(`Failed to download thumbnail for ${photo.id}`);
            }
            const blob = await thumbResponse.blob();
            photo.thumbnail_url = URL.createObjectURL(blob);
          } catch (error) {
            console.error(`Failed to fetch thumbnail ${photo.id}:`, error);
          }
        });
        
        await Promise.all(thumbnailPromises);
        
        // 新しいページのデータで置換
        setPhotoMeta(prev => ({ ...prev, currentPage: page }));
        
        // ダッシュボードの表示用データも更新
        setData(prev => ({ ...prev, photos }));
      }
    } catch (error) {
      console.error('Failed to load photo page:', error);
    } finally {
      setPhotoLoading(false);
    }
  }, [selectedDevice, photoMeta.pageSize, getDateRange, selectedDate, selectedHour]);



  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    console.log('🔍 useDashboardData handleWebSocketMessage called:', message);
    
    if (!message || !selectedDevice) {
      console.log('❌ No message or selectedDevice');
      return;
    }
    
    if (!message.device_id || !selectedDevice.id) {
      console.log('❌ No device_id in message or selectedDevice.id');
      return;
    }
    
    if (String(message.device_id) !== String(selectedDevice.id)) {
      console.log('❌ Device ID mismatch:', String(message.device_id), 'vs', String(selectedDevice.id));
      return;
    }
    
    console.log('✅ Processing WebSocket message:', message.type);
    
    switch (message.type) {
      case 'audio_uploaded':
        if (message.event_id) {
          console.log('🎵 Calling addNewAudio:', message.event_id);
          addNewAudio(message.event_id);
        }
        break;
      case 'photo_uploaded':
        if (message.event_id) {
          console.log('📷 Calling addNewPhoto:', message.event_id);
          addNewPhoto(message.event_id);
        }
        break;
      case 'gps_updated':
        if (message.event_id) {
          console.log('📍 Calling addNewGps:', message.event_id);
          addNewGps(message.event_id);
        }
        break;
      case 'transcribe.completed':
      case 'whisper.completed':
        if (message.data?.transcript_id) {
          console.log('📝 Calling updateTranscript:', message.data.transcript_id);
          updateTranscript(message.data.transcript_id, message.event_id);
        }
        break;
      case 'whisper.failed':
        if (message.event_id) {
          console.log('⚠️ Whisper failed, refreshing audio metadata:', message.event_id);
          addNewAudio(message.event_id);
        }
        break;
      default:
        console.log('❓ Unknown message type:', message.type);
    }
  }, [selectedDevice, addNewAudio, addNewPhoto, addNewGps, updateTranscript]);

  // デバイス変更時または日付変更時にデータを取得
  useEffect(() => {
    if (selectedDevice) {
      fetchTodayData();
    }
  }, [selectedDevice, selectedDate, fetchTodayData]);

  // BlobURLのクリーンアップ
  useEffect(() => {
    return () => {
      data.audio.forEach(audio => {
        if (audio.s3_url && audio.s3_url.startsWith('blob:')) {
          URL.revokeObjectURL(audio.s3_url);
        }
      });
      data.photos.forEach(photo => {
        if (photo.thumbnail_url && photo.thumbnail_url.startsWith('blob:')) {
          URL.revokeObjectURL(photo.thumbnail_url);
        }
      });
    };
  }, [data.audio, data.photos]);

  return {
    data,
    audioLoading,
    selectedDate,
    setSelectedDate,
    fetchTodayData,
    loadAudioFile,
    handleWebSocketMessage,
    photoMeta,
    loadPhotoPage,
    photoLoading,
    realtimeApiCalls,
    testApiCall
  };
};
